import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Alert, Container } from "react-bootstrap";
import { 
    CurrencyDollar, 
    CalendarCheck, 
    CalendarX, 
    People, 
    GraphUp 
} from "react-bootstrap-icons";

import { 
    getDashboardPrincipal, 
    getAdminAnalytics 
} from "../../../services/endpoints";

import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

// =========================
// CARD DE MÉTRICAS
// =========================
const MetricCard = ({ title, value, icon, corPrimaria, corSecundaria }) => {
    const bgStyle = {
        backgroundColor: corPrimaria || "#151515ff",
        color: corSecundaria || "#ffffff",
    };

    return (
        <Card className="shadow-sm w-100" style={bgStyle}>
            <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center gap-3">
                    <div>
                        <Card.Title className="h5 mb-0">{value}</Card.Title>
                        <Card.Text>{title}</Card.Text>
                    </div>
                    <div className="d-flex align-items-center">{icon}</div>
                </div>
            </Card.Body>
        </Card>
    );
};

// =========================
// GRÁFICO DE GANHOS ANUAIS
// =========================
const GraficoGanhosAnuais = ({ dados, corPrimaria }) => {
    if (!dados || dados.length === 0) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <Card.Title style={{ color: corPrimaria || "#151515ff" }}>
                        <GraphUp className="me-2" /> Ganhos Anuais ({new Date().getFullYear()})
                    </Card.Title>
                    <hr />
                    <p>Sem dados para exibir.</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <div className="d-flex justify-content-center align-items-start w-100">
            <Card className="shadow-sm w-100">
                <Card.Body>
                    <Card.Title style={{ color: corPrimaria || "#151515ff" }}>
                        <GraphUp className="me-2" /> Ganhos Anuais por Mês ({new Date().getFullYear()})
                    </Card.Title>
                    <hr />
                    <div style={{ width: "100%", height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={dados}
                                margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="nomeMes" />
                                <YAxis />
                                <Tooltip formatter={(value) => `R$ ${value.toFixed(2).replace(".", ",")}`} />
                                <Bar dataKey="ganhoTotal" fill={corPrimaria || "#151515ff"} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

// =========================
// DASHBOARD PRINCIPAL
// =========================
function DashboardMaster({ idClienteMaster }) {
    const [dashboardData, setDashboardData] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!idClienteMaster) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const dash = await getDashboardPrincipal(idClienteMaster);
                setDashboardData(dash);

                const analytics = await getAdminAnalytics(idClienteMaster);
                setAnalyticsData(analytics);

            } catch (err) {
                console.error("Erro Dashboard:", err);
                setError("Não foi possível carregar os dados do dashboard.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [idClienteMaster]);

    if (loading) return <div className="text-center p-5"><Spinner animation="border" /><p>Carregando Dashboard...</p></div>;
    if (error) return <Alert variant="danger">{error}</Alert>;

    const cores = dashboardData.cores || {};

    return (
        <Container fluid>

            <Row className="mb-4 g-3">
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Agendamentos Hoje"
                        value={dashboardData.agendamentosHoje}
                        icon={<CalendarCheck size={30} />}
                        corPrimaria={cores.agendamentosHoje?.primaria}
                        corSecundaria={cores.agendamentosHoje?.secundaria}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Lucro do Dia"
                        value={`R$ ${dashboardData.lucroDia.toFixed(2).replace('.', ',')}`}
                        icon={<CurrencyDollar size={30} />}
                        corPrimaria={cores.lucroDia?.primaria}
                        corSecundaria={cores.lucroDia?.secundaria}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Agendamentos Mês"
                        value={dashboardData.agendamentosMesRealizados}
                        icon={<CalendarCheck size={30} />}
                        corPrimaria={cores.agendamentosMesRealizados?.primaria}
                        corSecundaria={cores.agendamentosMesRealizados?.secundaria}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Lucro do Mês"
                        value={`R$ ${dashboardData.lucroMes.toFixed(2).replace('.', ',')}`}
                        icon={<CurrencyDollar size={30} />}
                        corPrimaria={cores.lucroMes?.primaria}
                        corSecundaria={cores.lucroMes?.secundaria}
                    />
                </Col>
            </Row>

            <Row className="mb-4 g-3">
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Pendentes"
                        value={dashboardData.agendamentosPendentes}
                        icon={<CalendarX size={30} />}
                        corPrimaria={cores.agendamentosPendentes?.primaria}
                        corSecundaria={cores.agendamentosPendentes?.secundaria}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Total de Funcionários"
                        value={analyticsData.totalFuncionarios}
                        icon={<People size={30} />}
                        corPrimaria={cores.totalFuncionarios?.primaria}
                        corSecundaria={cores.totalFuncionarios?.secundaria}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Total de Clientes"
                        value={analyticsData.totalClientes}
                        icon={<People size={30} />}
                        corPrimaria={cores.totalClientes?.primaria}
                        corSecundaria={cores.totalClientes?.secundaria}
                    />
                </Col>
            </Row>

            <Row className="mb-4 g-3">
                <Col xs={12}>
                    <GraficoGanhosAnuais 
                        dados={analyticsData.ganhosAnuais} 
                        corPrimaria={cores.graficoGanhosAnuais?.primaria} 
                    />
                </Col>
            </Row>

        </Container>
    );
}

export default DashboardMaster;
